import { useParams } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { useApi } from "@/lib/api";
import { DarkHero, Loader, Empty, FixtureRow, AthleteCard, NewsRow, SectionHead } from "@/components/Blocks";
import { fmtDate } from "@/lib/utils";
import { StandingsTable } from "@/components/StandingsTable";
import { useT } from "@/lib/i18n";

const Meta = ({ item }) => (
  <p className="mt-4 text-xs text-slate-400">Source: {item.source}{item.source_url && <> · <a href={item.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#047857] hover:underline">Wikipedia <ExternalLink size={11} /></a></>}{item.official_url && <> · <a href={item.official_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#047857] hover:underline">Official site <ExternalLink size={11} /></a></>} · Fetched {fmtDate(item.fetched_at, false)}</p>
);

export function TeamPage() {
  const { slug } = useParams();
  const { data: t, isLoading, error } = useApi("team", `/teams/${slug}`);
  if (isLoading) return <Loader />;
  if (error || !t) return <div className="container-x py-20"><Empty text="Team not found." /></div>;
  return (
    <div data-testid="team-page">
      <DarkHero title={t.name} subtitle={`${t.kind} · "${t.nickname}"${t.stadium ? ` · ${t.stadium}` : ""}`} image={t.banner || t.image} small>
        {t.badge && <img src={t.badge} alt="" className="mt-6 h-24 w-24 object-contain drop-shadow-xl" />}
      </DarkHero>
      <div className="container-x grid gap-10 py-10 lg:grid-cols-[1fr_360px]">
        <div>
          <h2 className="font-display text-2xl font-extrabold">About</h2>
          <p className="mt-3 leading-relaxed text-slate-700" data-testid="team-about">{t.about}</p>
          <Meta item={t} />
          <div className="mt-10"><SectionHead title="Fixtures & Results" id="team-fixtures" /><div className="space-y-2">{t.fixtures?.length ? t.fixtures.map((f) => <FixtureRow key={f.id} f={f} />) : <Empty text="No fixtures available from connected online sources for this team." />}</div></div>
          <div className="mt-10"><SectionHead title="Players to Watch" id="team-players" /><div className="grid grid-cols-2 gap-4 sm:grid-cols-4">{t.players?.length ? t.players.map((a) => <AthleteCard key={a.slug} a={a} showSport={false} />) : <Empty />}</div></div>
        </div>
        <aside>
          <div className="rounded-xl border border-slate-100 bg-slate-50 p-5">
            <h3 className="font-display text-xl font-extrabold">Facts</h3>
            <dl className="mt-3 space-y-2 text-sm">{[["Founded", t.founded], ["Stadium", t.stadium], ["Location", t.location], ["Website", t.website], ["Type", t.kind]].filter(([, v]) => v).map(([k, v]) => <div key={k} className="flex justify-between gap-4 border-b border-slate-200/60 pb-2 last:border-0"><dt className="text-slate-500">{k}</dt><dd className="text-right font-semibold">{v}</dd></div>)}</dl>
          </div>
          <h3 className="mt-8 font-display text-2xl font-extrabold">Related News</h3>
          <div>{t.news?.map((n) => <NewsRow key={n.slug} item={n} />)}</div>
        </aside>
      </div>
    </div>
  );
}

export function CompetitionPage() {
  const { slug } = useParams();
  const { t } = useT();
  const { data: c, isLoading, error } = useApi("competition", `/competitions/${slug}`);
  if (isLoading) return <Loader />;
  if (error || !c) return <div className="container-x py-20"><Empty text="Competition not found." /></div>;
  return (
    <div data-testid="competition-page">
      <DarkHero title={c.name} subtitle={c.summary} image={c.image} small>{c.logo && <img src={c.logo} alt="" className="mt-6 h-20 object-contain drop-shadow-xl" />}</DarkHero>
      <div className="container-x grid gap-10 py-10 lg:grid-cols-[1fr_360px]">
        <div>
          {c.standings?.length > 0 && <div className="mb-10"><SectionHead title="Standings" id="competition-standings" /><div className="space-y-6">{c.standings.map((s) => <StandingsTable key={s.slug} s={s} />)}</div></div>}
          <h2 className="font-display text-2xl font-extrabold">{t("About")}</h2>
          <p className="mt-3 leading-relaxed text-slate-700" data-testid="competition-about">{c.about}</p>
          <Meta item={c} />
          <div className="mt-10"><SectionHead title="Matches" id="competition-fixtures" /><div className="space-y-2">{c.fixtures?.length ? c.fixtures.map((f) => <FixtureRow key={f.id} f={f} />) : <Empty text="Standings and match data for this competition are not available from connected free sources yet." />}</div></div>
        </div>
        <aside><h3 className="font-display text-2xl font-extrabold">{t("Related News")}</h3><div>{c.news?.map((n) => <NewsRow key={n.slug} item={n} />)}</div></aside>
      </div>
    </div>
  );
}
